import React, { useState, useEffect } from "react";
import Button from "@mui/material/Button";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import PageHeading from "components/common/LayoutComponents/PageHeading";
import PageContent from "components/common/LayoutComponents/PageContent";
import { getRoles, getPermissionsByModule } from "services/permission";
import { verifyResponse } from "utils/httpRequest";
import LoadingScreen from "components/common/LoadingScreen";
import { useSnackbar } from "notistack";
import { PERMISSIONS } from "constants/permission";
import { UsePermissionVerification } from "components/customHooks";
import RoleDlg from "./RoleDlg";
import RoleDetail from "./RoleDetail";
import RoleTable from "./RoleTable";
import { StyledRole } from "./Role.style";

const Role = () => {
  const [roleLoading, setRoleLoading] = useState(false);
  const [permissionLoading, setPermissionLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [permissionByModule, setPermissionByModule] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const AccessControl = UsePermissionVerification();
  const [openRoleDialog, roleDialog, roleDialogLoading] = RoleDlg({
    onSubmit: () => {
      loadRoles();
    },
  });

  useEffect(() => {
    AccessControl.SetAccess({
      ...AccessControl.Access,
      searchRoleAccess: AccessControl.VerifyAccess([
        PERMISSIONS.ROLE.ROLE_SEARCH,
      ]),
      addRoleAccess: AccessControl.VerifyAccess([PERMISSIONS.ROLE.ROLE_ADD]),
      editRoleAccess: AccessControl.VerifyAccess([PERMISSIONS.ROLE.ROLE_EDIT]),
      deleteRoleAccess: AccessControl.VerifyAccess([
        PERMISSIONS.ROLE.ROLE_DELETE,
      ]),
      getDetailByIdRoleAccess: AccessControl.VerifyAccess([
        PERMISSIONS.ROLE.ROLE_GET_DETAIL_BY_ID,
      ]),
      setPermissionRoleAccess: AccessControl.VerifyAccess([
        PERMISSIONS.ROLE.ROLE_SET_PERMISSION,
      ]),
      getListByIdRoleAccess: AccessControl.VerifyAccess([
        PERMISSIONS.ROLE.ROLE_LIST_BY_ID,
      ]),
    });
  }, [AccessControl.UserPermissions]);

  const loadRoles = async () => {
    setRoleLoading(true);
    try {
      const response = await getRoles();
      verifyResponse(response);
      setRoles(response.data.list || []);
    } catch {
      setRoles([]);
      enqueueSnackbar("Failed to load roles.", { variant: "warning" });
    } finally {
      setRoleLoading(false);
    }
  };

  const loadPermissionsByModule = async () => {
    setPermissionLoading(true);
    try {
      const response = await getPermissionsByModule();
      verifyResponse(response);
      setPermissionByModule(response.data || []);
    } catch {
      setPermissionByModule([]);
      enqueueSnackbar("Failed to load permission modules.", {
        variant: "warning",
      });
    } finally {
      setPermissionLoading(false);
    }
  };

  const closeRoleDetail = () => {
    setSelectedRole(null);
  };

  useEffect(() => {
    loadRoles();
    loadPermissionsByModule();
  }, []);

  const [roleDetailContent, roleDetailLoading] = RoleDetail({
    role: selectedRole,
    access: AccessControl.Access,
    permissionByModule,
  });

  return (
    <PageContent>
      <StyledRole>
        <PageHeading>
          <div className="heading-copy">
            <h1 className="page-title">Roles</h1>
            <p className="page-subtitle">
              Manage role definitions and assign permission scopes by module.
            </p>
          </div>
          <div className="actions">
            {selectedRole && (
              <Button
                className="back-btn"
                variant="outlined"
                size="medium"
                startIcon={<ArrowBackRoundedIcon />}
                onClick={() => {
                  closeRoleDetail();
                }}
              >
                Back to list
              </Button>
            )}
            {!selectedRole && AccessControl.Access.addRoleAccess && (
              <Button
                className="new-role-btn"
                variant="contained"
                size="medium"
                startIcon={<AddCircleOutlineRoundedIcon />}
                onClick={() => {
                  openRoleDialog();
                }}
              >
                Add Role
              </Button>
            )}
          </div>
        </PageHeading>
        <div className="page-surface">
          {!AccessControl.Access.searchRoleAccess && (
            <div className="page-empty">
              You do not have permission to view role records.
            </div>
          )}
          {AccessControl.Access.searchRoleAccess && !selectedRole && (
            <RoleTable
              roles={roles}
              access={AccessControl.Access}
              setSelectedRole={setSelectedRole}
              openRoleDialog={openRoleDialog}
            />
          )}
          {AccessControl.Access.searchRoleAccess && selectedRole && roleDetailContent}
        </div>
      </StyledRole>
      <LoadingScreen
        loading={
          roleLoading ||
          roleDialogLoading ||
          permissionLoading ||
          roleDetailLoading
        }
      />
      {roleDialog}
    </PageContent>
  );
};

export default Role;
